import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useAuthStore from '../../context/authStore';
import { 
  Search, 
  Filter, 
  Clock, 
  Award, 
  BookOpen,
  ChevronRight,
  Play,
  CheckCircle,
  AlertTriangle,
  Info,
} from 'lucide-react';

const categories = [
  { id: 'all', name: 'All Courses' },
  { id: 'compliance', name: 'Compliance' },
  { id: 'clinical_skills', name: 'Clinical Skills' },
  { id: 'medication', name: 'Medication' },
  { id: 'role_specific', name: 'Role-Specific' },
  { id: 'leadership_management', name: 'Leadership' },
  { id: 'professional_development', name: 'Professional Development' },
  { id: 'specialty', name: 'Specialty' },
  { id: 'onboarding', name: 'Onboarding' },
];

export default function CourseCatalogPage() {
  const { user } = useAuthStore();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [myLearning, setMyLearning] = useState(null);

  useEffect(() => {
    fetchCourses();
    fetchMyLearning();
  }, [selectedCategory, searchTerm]);

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);
      
      const res = await fetch(`/api/lms/courses?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const rawCourses = data.courses || [];
      const visibleCourses = ['facility_worker', 'worker'].includes(user?.role)
        ? rawCourses.filter((course) => !!course.enrollment || course.accessPolicy?.canSelfEnroll)
        : rawCourses;
      setCourses(visibleCourses);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyLearning = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/lms/enrollments/my-learning', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setMyLearning(data);
    } catch (error) {
      console.error('Failed to fetch my learning:', error);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      compliance: 'bg-blue-100 text-blue-700',
      clinical_skills: 'bg-green-100 text-green-700',
      medication: 'bg-rose-100 text-rose-700',
      role_specific: 'bg-cyan-100 text-cyan-700',
      leadership_management: 'bg-violet-100 text-violet-700',
      professional_development: 'bg-amber-100 text-amber-700',
      specialty: 'bg-fuchsia-100 text-fuchsia-700',
      onboarding: 'bg-amber-100 text-amber-700',
      default: 'bg-gray-100 text-gray-700',
    };
    return colors[category] || colors.default;
  };

  const formatCategoryLabel = (course) => {
    const labelSource = course.catalogGroup || course.category;
    return String(labelSource || 'other').replace(/_/g, ' ');
  };

  const getEnrollmentStatus = (enrollment) => {
    if (!enrollment) return null;
    if (enrollment.status === 'completed') {
      return { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle };
    }
    return { 
      label: `${enrollment.progress}% Complete`, 
      color: 'bg-blue-100 text-blue-700', 
      icon: Play,
    };
  };

  const getAvailabilityStatus = (course) => {
    if (course.explanation?.blockingReasons?.length) {
      return {
        label: 'Blocked',
        color: 'bg-amber-100 text-amber-700',
        icon: AlertTriangle,
      };
    }
    if (course.enrollment) return getEnrollmentStatus(course.enrollment);
    if (course.accessPolicy?.canSelfEnroll) {
      return {
        label: course.accessPolicy?.distributionMode === 'hybrid' ? 'Available to start' : 'Self-enroll',
        color: 'bg-emerald-100 text-emerald-700',
        icon: Play,
      };
    }
    return null;
  };

  const getExplanationPreview = (course) => {
    const explanation = course.explanation || {};
    return [
      ...(explanation.blockingReasons || []),
      ...(explanation.assignabilityReasons || []),
      ...(explanation.supportingReasons || []),
      ...(explanation.visibilityReasons || []),
    ].slice(0, 2);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-gray-900">Training Catalog</h1>
        <p className="text-gray-600 mt-1">
          {user?.role === 'worker'
            ? 'Courses assigned to you'
            : user?.role === 'facility_worker'
              ? 'Assigned courses and any training your facility has released for self-start'
              : 'Discover and explore available training courses'}
        </p>
      </div>

      {/* My Learning Summary */}
      {myLearning && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-2xl font-bold text-gray-900">{myLearning.stats.inProgressCourses}</p>
            <p className="text-sm text-gray-500">In Progress</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-2xl font-bold text-gray-900">{myLearning.stats.completedCourses}</p>
            <p className="text-sm text-gray-500">Completed</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-2xl font-bold text-gray-900">{myLearning.stats.certificates}</p>
            <p className="text-sm text-gray-500">Certificates</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-2xl font-bold text-gray-900">{myLearning.stats.totalTimeSpent}m</p>
            <p className="text-sm text-gray-500">Time Spent</p>
          </div>
        </div>
      )}

      {/* Continue Learning */}
      {myLearning?.inProgress?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Continue Learning</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {myLearning.inProgress.slice(0, 3).map(enrollment => (
              <Link
                key={enrollment._id}
                to={`/training/${enrollment.courseId._id}`}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Play className="w-6 h-6 text-primary-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{enrollment.courseId.title}</p>
                  <div
                    className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden"
                    role="progressbar"
                    aria-valuenow={enrollment.progress.percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${enrollment.courseId.title} progress`}
                  >
                    <div 
                      className="h-full bg-primary-500 rounded-full"
                      style={{ width: `${enrollment.progress.percentage}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{enrollment.progress.percentage}% complete</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" aria-hidden="true" />
          <label htmlFor="course-search" className="sr-only">Search courses</label>
          <input
            id="course-search"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search courses..."
            className="input pl-10"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2" role="group" aria-label="Filter by category">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              aria-pressed={selectedCategory === cat.id}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === cat.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Course Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64" role="status" aria-label="Loading courses">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" aria-hidden="true" />
        </div>
      ) : courses.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" aria-hidden="true" />
          <h3 className="text-lg font-medium text-gray-900">No courses found</h3>
          <p className="text-gray-500 mt-1">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map(course => {
            const status = getAvailabilityStatus(course);
            
            return (
              <Link
                key={course._id}
                to={`/training/${course._id}`}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group"
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-gray-100 relative">
                  {course.thumbnail ? (
                    <img 
                      src={course.thumbnail} 
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700">
                      <BookOpen className="w-12 h-12 text-white/80" />
                    </div>
                  )}
                  
                  {/* Status badge */}
                  {status && (
                    <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${status.color}`}>
                      <status.icon size={12} aria-hidden="true" />
                      {status.label}
                    </div>
                  )}
                  
                  {/* Play overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center" aria-hidden="true">
                    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Play className="w-5 h-5 text-gray-900 ml-0.5" />
                    </div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor(course.catalogGroup || course.category)}`}>
                      {formatCategoryLabel(course)}
                    </span>
                    {course.ceCredits > 0 && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                        {course.ceCredits} CE
                      </span>
                    )}
                  </div>
                  
                  <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {course.title}
                  </h3>
                  
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                    {course.shortDescription || course.description}
                  </p>

                  {getExplanationPreview(course).length > 0 && (
                    <div className="mt-3 space-y-1">
                      {getExplanationPreview(course).map((reason) => (
                        <div key={reason.code} className="flex items-start gap-2 text-xs text-gray-600">
                          <Info size={12} className="mt-0.5 text-gray-400" aria-hidden="true" />
                          <span className="line-clamp-2">{reason.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                   
                  <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock size={14} aria-hidden="true" />
                      {course.estimatedDuration} min
                    </span>
                    {course.certificateEnabled && (
                      <span className="flex items-center gap-1">
                        <Award size={14} aria-hidden="true" />
                        Certificate
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
